import { useState, useCallback } from "react";
import { useNotify } from "ra-core";
import { Card, CardContent } from "@/components/ui/card";
import {
  CreditCard,
  ExternalLink,
  Loader2,
  CheckCircle2,
  AlertTriangle,
  Clock,
  XCircle,
  Shield,
} from "lucide-react";

interface StripeConnectCardProps {
  orgId: string;
  stripeAccountId: string | null;
  stripeAccountStatus: string;
  stripeOnboardedAt: string | null;
  platformFeePct: number;
  platformFeeFixed: number;
}

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL || "";

const STATUS_CONFIG: Record<
  string,
  {
    label: string;
    icon: React.ComponentType<{ className?: string }>;
    color: string;
    bg: string;
    description: string;
  }
> = {
  none: {
    label: "Not Connected",
    icon: XCircle,
    color: "#6b7280",
    bg: "#f3f4f6",
    description: "Connect your Stripe account to accept payments from customers.",
  },
  pending: {
    label: "Pending",
    icon: Clock,
    color: "#d97706",
    bg: "#fef3c7",
    description: "Stripe onboarding started. Complete it to enable payments.",
  },
  verified: {
    label: "Verified",
    icon: CheckCircle2,
    color: "#16a34a",
    bg: "#dcfce7",
    description: "Your Stripe account is active and can accept payments.",
  },
  restricted: {
    label: "Restricted",
    icon: AlertTriangle,
    color: "#dc2626",
    bg: "#fee2e2",
    description: "Stripe requires additional information. Complete onboarding to re-enable.",
  },
  disabled: {
    label: "Disabled",
    icon: XCircle,
    color: "#dc2626",
    bg: "#fee2e2",
    description: "Your Stripe account has been disabled. Contact support.",
  },
};

export function StripeConnectCard({
  orgId,
  stripeAccountId,
  stripeAccountStatus,
  stripeOnboardedAt,
  platformFeePct,
  platformFeeFixed,
}: StripeConnectCardProps) {
  const [connecting, setConnecting] = useState(false);
  const notify = useNotify();

  const config = STATUS_CONFIG[stripeAccountStatus] || STATUS_CONFIG.none;
  const StatusIcon = config.icon;

  const handleConnect = useCallback(async () => {
    setConnecting(true);
    try {
      // Get auth token
      const { createClient } = await import(
        "https://esm.sh/@supabase/supabase-js@2"
      );
      const sb = createClient(
        SUPABASE_URL,
        import.meta.env.VITE_SUPABASE_ANON_KEY || ""
      );
      const {
        data: { session },
      } = await sb.auth.getSession();

      if (!session?.access_token) {
        notify("Please log in again", { type: "error" });
        return;
      }

      const res = await fetch(
        `${SUPABASE_URL}/functions/v1/stripe-connect-onboard`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${session.access_token}`,
          },
          body: JSON.stringify({}),
        }
      );

      const data = await res.json();
      if (!res.ok) throw new Error(data.error || "Failed");

      // Redirect to Stripe's hosted onboarding
      if (data.url) {
        window.location.href = data.url;
      }
    } catch (err) {
      notify(`Stripe Connect failed: ${(err as Error).message}`, {
        type: "error",
      });
    } finally {
      setConnecting(false);
    }
  }, [notify]);

  return (
    <Card>
      <CardContent className="p-5">
        <div className="flex items-start gap-4">
          <div
            className="flex items-center justify-center w-10 h-10 rounded-xl shrink-0"
            style={{ backgroundColor: "#ede9fe" }}
          >
            <CreditCard className="w-5 h-5 text-purple-600" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 mb-1">
              <h3 className="text-sm font-semibold">Stripe Payments</h3>
              <span
                className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium"
                style={{ color: config.color, backgroundColor: config.bg }}
              >
                <StatusIcon className="w-3 h-3" />
                {config.label}
              </span>
            </div>

            <p className="text-xs text-muted-foreground mb-3">
              {config.description}
            </p>

            {/* Account details when connected */}
            {stripeAccountId && (
              <div className="flex flex-wrap gap-3 text-xs text-muted-foreground mb-3">
                <span className="font-mono">{stripeAccountId}</span>
                {stripeOnboardedAt && (
                  <span>
                    Connected{" "}
                    {new Date(stripeOnboardedAt).toLocaleDateString()}
                  </span>
                )}
              </div>
            )}

            {/* Platform fee info */}
            {stripeAccountStatus === "verified" && (
              <div className="flex items-center gap-1.5 text-xs text-muted-foreground mb-3">
                <Shield className="w-3 h-3" />
                Platform fee: {(platformFeePct * 100).toFixed(1)}% + $
                {platformFeeFixed.toFixed(2)} per transaction
              </div>
            )}

            {/* Action buttons */}
            <div className="flex gap-2">
              {(stripeAccountStatus === "none" ||
                stripeAccountStatus === "pending" ||
                stripeAccountStatus === "restricted") && (
                <button
                  onClick={handleConnect}
                  disabled={connecting}
                  className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-medium text-white bg-purple-600 hover:bg-purple-700 transition-colors disabled:opacity-50"
                >
                  {connecting ? (
                    <Loader2 className="w-3.5 h-3.5 animate-spin" />
                  ) : (
                    <ExternalLink className="w-3.5 h-3.5" />
                  )}
                  {stripeAccountStatus === "none"
                    ? "Connect Stripe"
                    : "Continue Setup"}
                </button>
              )}
              {stripeAccountId && (
                <a
                  href={`https://dashboard.stripe.com/${stripeAccountId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="flex items-center gap-1.5 px-3 py-2 rounded-lg text-sm font-medium border text-gray-700 hover:bg-gray-50 transition-colors"
                >
                  <ExternalLink className="w-3.5 h-3.5" />
                  Stripe Dashboard
                </a>
              )}
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
}
